import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { KpiBar } from "@/components/KpiBar";
import { SpreadsheetTable } from "@/components/SpreadsheetTable";
import { SmartCaptureModal } from "@/components/SmartCaptureModal";
import { AiReviewDrawer } from "@/components/AiReviewDrawer";
import { ImageLightbox } from "@/components/ImageLightbox";
import { ResetConfirmDialog } from "@/components/ResetConfirmDialog";
import { FieldsSettings } from "@/components/FieldsSettings";
import { api } from "@/lib/apiClient";
import { loadLocal, saveLocal, exportCsv } from "@/lib/storage";
import { useFields } from "@/context/FieldsContext";

const SEED = [
  {
    item_name: "DeWalt 20V MAX Cordless Drill Set",
    description: "Brushless cordless drill/driver with 2 batteries and charger case.",
    category: "Power Tools",
    quantity: 4,
    extracted_text: "DeWalt DCD777C2 20V MAX",
    source_image_url: "https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=400&q=80",
  },
  {
    item_name: "Stainless M8 Industrial Bolts",
    description: "Box of 500 corrosion-resistant hex bolts, 8mm.",
    category: "Hardware",
    quantity: 12,
    extracted_text: "M8 x 40mm · A2-70",
    source_image_url: "https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&w=400&q=80",
  },
  {
    item_name: "CAT6 Shielded Ethernet Cable 1000ft",
    description: "Industrial shielded twisted pair networking cable spool.",
    category: "Electronics",
    quantity: 2,
    extracted_text: "CAT6 STP 1000FT 23AWG",
    source_image_url: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=400&q=80",
  },
  {
    item_name: "Heavy Duty Steel Shelving Unit",
    description: "5-tier powder-coated steel warehouse storage rack.",
    category: "Storage Equipment",
    quantity: 1,
    extracted_text: "",
    source_image_url: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=400&q=80",
  },
];

export default function InventoryDashboard() {
  const { user, logout } = useAuth();
  const { config } = useFields();
  const uid = user?.user_id;
  const [items, setItems] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [staged, setStaged] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const seeded = useRef(false);

  // Persist to localStorage (scoped per user) whenever items change
  useEffect(() => {
    if (uid) saveLocal(uid, items);
  }, [items, uid]);

  const refresh = useCallback(async () => {
    try {
      const data = await api.list();
      if (data.length === 0 && !seeded.current && !loadLocal(uid)?.length) {
        seeded.current = true;
        const created = await api.createBatch(SEED);
        setItems(created);
      } else {
        setItems(data);
      }
    } catch (e) {
      console.error("Backend unavailable, using local data", e);
    }
  }, [uid]);

  useEffect(() => {
    const cached = loadLocal(uid);
    if (cached) setItems(cached);
    refresh();
  }, [refresh, uid]);

  const handleAdd = async (draft) => {
    try {
      const created = await api.create(draft);
      setItems((prev) => [created, ...prev]);
      toast.success("Item added");
    } catch {
      toast.error("Could not add item");
    }
  };

  const handleUpdate = async (id, updates) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...updates } : i)));
    try {
      await api.update(id, updates);
    } catch {
      toast.error("Update failed");
      refresh();
    }
  };

  const handleDelete = async (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    try {
      await api.remove(id);
      toast.success("Item deleted");
    } catch {
      toast.error("Delete failed");
      refresh();
    }
  };

  const handleBulkDelete = async (ids) => {
    const idSet = new Set(ids);
    setItems((prev) => prev.filter((i) => !idSet.has(i.id)));
    try {
      await api.bulkDelete(ids);
      toast.success(`Deleted ${ids.length} item${ids.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Bulk delete failed");
      refresh();
    }
  };

  const handleImport = async (file) => {
    try {
      const created = await api.importFile(file);
      setItems((prev) => [...created, ...prev]);
      toast.success(`Imported ${created.length} item${created.length === 1 ? "" : "s"} from ${file.name}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Import failed. Check the file format.";
      toast.error(msg);
    }
  };

  const handleProcessed = (stagedItems) => {
    setStaged(stagedItems);
    setReviewOpen(true);
  };

  const handleBarcode = async (code) => {
    const payload = {
      item_name: `Item ${code}`,
      description: "Captured via barcode scan",
      category: "Scanned",
      quantity: 1,
      extracted_text: code,
      fields: {},
    };
    try {
      const created = await api.create(payload);
      setItems((prev) => [created, ...prev]);
      toast.success(`Barcode logged: ${code}`);
    } catch {
      toast.error("Could not add scanned item");
    }
  };

  const handleConfirmBatch = async (payload) => {
    try {
      const created = await api.createBatch(payload);
      setItems((prev) => [...created, ...prev]);
      setReviewOpen(false);
      setStaged([]);
      toast.success(`Added ${created.length} item${created.length === 1 ? "" : "s"} to inventory!`);
    } catch {
      toast.error("Batch add failed");
    }
  };

  const handleExport = () => {
    if (items.length === 0) return toast.error("Nothing to export");
    exportCsv(items, config.fields);
    toast.success("CSV exported");
  };

  const handleClear = async () => {
    try {
      await api.clear();
    } catch {
      /* ignore */
    }
    setItems([]);
    saveLocal(uid, []);
    setResetOpen(false);
    toast.success("Inventory cleared");
  };

  return (
    <div className="min-h-screen grid-texture">
      <Header
        itemCount={items.length}
        onScan={() => setScanOpen(true)}
        onExport={handleExport}
        onClear={() => setResetOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
        user={user}
        onLogout={logout}
      />

      <main className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-50">
              Inventory Spreadsheet
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Snap or upload photos — AI extracts text, names & categories, then exports to CSV.
            </p>
          </div>
        </div>

        <KpiBar items={items} />

        <SpreadsheetTable
          items={items}
          onAdd={handleAdd}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onImageClick={setLightbox}
          onBulkDelete={handleBulkDelete}
          onImport={handleImport}
        />
      </main>

      <SmartCaptureModal
        open={scanOpen}
        onOpenChange={setScanOpen}
        onProcessed={handleProcessed}
        onBarcodeDetected={handleBarcode}
      />
      <AiReviewDrawer
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        stagedItems={staged}
        onConfirm={handleConfirmBatch}
        onEditFields={() => setSettingsOpen(true)}
      />
      <FieldsSettings open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ImageLightbox item={lightbox} onClose={() => setLightbox(null)} />
      <ResetConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        onConfirm={handleClear}
        count={items.length}
      />
    </div>
  );
}
